/**
 * Ticket API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  Ticket,
  TicketDetail,
  TicketCreateRequest,
  TicketUpdateRequest,
  TicketSystem,
  TicketFunction,
  TicketMessage,
  TicketAttachment,
} from '../../types';

const API_PREFIX = '/api/saiens/ticket';

interface TicketListParams {
  offset?: number;
  limit?: number;
  type?: string;
  state?: string;
  search?: string;
  show_all?: boolean;
}

/**
 * Get active ticket systems
 */
export async function getTicketSystems(): Promise<ApiResponse<TicketSystem[]>> {
  const response = await apiClient.get<ApiResponse<TicketSystem[]>>(
    `${API_PREFIX}/systems`,
  );
  return response.data;
}

/**
 * Get active ticket functions (optionally filtered by system_id)
 */
export async function getTicketFunctions(
  systemId?: number,
): Promise<ApiResponse<TicketFunction[]>> {
  const params: Record<string, unknown> = {};
  if (systemId) params.system_id = systemId;

  const response = await apiClient.get<ApiResponse<TicketFunction[]>>(
    `${API_PREFIX}/functions`,
    { params },
  );
  return response.data;
}

/**
 * Create a new ticket system
 */
export async function createTicketSystem(
  data: { name: string; code?: string },
): Promise<ApiResponse<TicketSystem>> {
  const response = await apiClient.post<ApiResponse<TicketSystem>>(
    `${API_PREFIX}/systems/create`,
    data,
  );
  return response.data;
}

/**
 * Create a new ticket function
 */
export async function createTicketFunction(
  data: { name: string; system_id: number },
): Promise<ApiResponse<TicketFunction>> {
  const response = await apiClient.post<ApiResponse<TicketFunction>>(
    `${API_PREFIX}/functions/create`,
    data,
  );
  return response.data;
}

/**
 * List tickets with filters
 */
export async function getTicketList(
  params: TicketListParams = {},
): Promise<ApiResponse<PaginatedData<Ticket>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<Ticket>>>(
    `${API_PREFIX}/list`,
    { params },
  );
  return response.data;
}

/**
 * Get ticket detail
 */
export async function getTicket(
  id: number,
): Promise<ApiResponse<TicketDetail>> {
  const response = await apiClient.get<ApiResponse<TicketDetail>>(
    `${API_PREFIX}/${id}`,
  );
  return response.data;
}

/**
 * Create a new ticket
 */
export async function createTicket(
  data: TicketCreateRequest,
): Promise<ApiResponse<TicketDetail>> {
  const response = await apiClient.post<ApiResponse<TicketDetail>>(
    `${API_PREFIX}/create`,
    data,
  );
  return response.data;
}

/**
 * Update a ticket (draft only)
 */
export async function updateTicket(
  id: number,
  data: TicketUpdateRequest,
): Promise<ApiResponse<TicketDetail>> {
  const response = await apiClient.put<ApiResponse<TicketDetail>>(
    `${API_PREFIX}/${id}`,
    data,
  );
  return response.data;
}

/**
 * Submit a ticket
 */
export async function submitTicket(
  id: number,
): Promise<ApiResponse<TicketDetail>> {
  const response = await apiClient.post<ApiResponse<TicketDetail>>(
    `${API_PREFIX}/${id}/submit`,
  );
  return response.data;
}

/**
 * User confirm test result
 */
export async function userConfirmTicket(
  id: number,
  confirmed: boolean,
): Promise<ApiResponse<TicketDetail>> {
  const response = await apiClient.post<ApiResponse<TicketDetail>>(
    `${API_PREFIX}/${id}/user-confirm`,
    { confirmed },
  );
  return response.data;
}

/**
 * Delete a draft ticket
 */
export async function deleteTicket(
  id: number,
): Promise<ApiResponse<void>> {
  const response = await apiClient.delete<ApiResponse<void>>(
    `${API_PREFIX}/${id}`,
  );
  return response.data;
}

/**
 * Upload attachment to ticket
 */
export async function uploadTicketImage(
  ticketId: number,
  file: File,
): Promise<ApiResponse<TicketAttachment>> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<ApiResponse<TicketAttachment>>(
    `${API_PREFIX}/${ticketId}/upload-image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

/**
 * Delete attachment from ticket
 */
export async function deleteTicketImage(
  ticketId: number,
  attachmentId: number,
): Promise<ApiResponse<void>> {
  const response = await apiClient.post<ApiResponse<void>>(
    `${API_PREFIX}/${ticketId}/delete-image`,
    { attachment_id: attachmentId },
  );
  return response.data;
}

/**
 * Get ticket messages
 */
export async function getTicketMessages(
  ticketId: number,
): Promise<ApiResponse<TicketMessage[]>> {
  const response = await apiClient.get<ApiResponse<TicketMessage[]>>(
    `${API_PREFIX}/${ticketId}/messages`,
  );
  return response.data;
}

/**
 * Add message to ticket
 */
export async function createTicketMessage(
  ticketId: number,
  content: string,
): Promise<ApiResponse<TicketMessage>> {
  const response = await apiClient.post<ApiResponse<TicketMessage>>(
    `${API_PREFIX}/${ticketId}/messages/create`,
    { content },
  );
  return response.data;
}
